/**
 * useTeamLocations — Real-time Firestore listener for all team member positions.
 *
 * Subscribes to the tech_states collection and extracts liveLocation data.
 * People are NEVER removed from the map — they persist at their last-seen
 * position forever. Each location is tagged with a freshness tier (live /
 * idle / offline) so the map can dim + label it accordingly. Tiers are
 * recomputed on a 30s ticker so they stay accurate even when no new Firestore
 * writes are coming in (e.g. everyone has the app closed).
 */

import { useState, useEffect, useMemo } from 'react';
import { db } from '@/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useTenant } from '@/hooks/useTenant';
import { useTeam } from '@/hooks/useTeam';
import { LOCATION_RECENT_MS, LOCATION_OFFLINE_MS } from '@/data/map-config';

export interface TeamLocation {
  userId: string;
  name: string;
  role: string;
  lat: number;
  lng: number;
  lastSeen: string;
  accuracy?: number;
  isRecent: boolean; // < 5 minutes old → "online", full opacity + pulse
  isOffline: boolean; // > 30 minutes old → greyed "last seen X ago" pin
}

interface RawTeamLocation {
  userId: string;
  storedName?: string;
  role: string;
  lat: number;
  lng: number;
  lastSeen: string;
  accuracy?: number;
}

export function useTeamLocations(): { locations: TeamLocation[]; loading: boolean } {
  const { tenant } = useTenant();
  const { team } = useTeam();
  const [rawLocations, setRawLocations] = useState<RawTeamLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const tenantId = tenant?.id;

  // Re-evaluate freshness tiers every 30s so live → idle → offline transitions
  // happen even when no new Firestore writes are arriving (app closed).
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'tech_states'),
      where('tenantId', '==', tenantId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const locs: RawTeamLocation[] = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const live = data.liveLocation;
          if (
            !live ||
            typeof live.lat !== 'number' ||
            typeof live.lng !== 'number' ||
            !live.lastSeen
          ) {
            return;
          }

          // No staleness filter — everyone with a known position stays on the
          // map. Freshness is conveyed by the tier (computed below), not by
          // dropping the marker.
          locs.push({
            userId: doc.id,
            storedName: live.userName,
            role: live.userRole || 'Unknown',
            lat: live.lat,
            lng: live.lng,
            lastSeen: live.lastSeen,
            accuracy: live.accuracy,
          });
        });

        setRawLocations(locs);
        setLoading(false);
      },
      () => {
        // Error — silent, just stop loading
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [tenantId]);

  // Resolve name from users collection first, fall back to denormalized stored
  // name, and only use the userId as a last resort (avoids raw IDs on the map).
  // Freshness tiers are derived here against nowTick so they stay current.
  const locations = useMemo<TeamLocation[]>(() => {
    const nameById = new Map<string, string>();
    team.forEach((t) => {
      if (t.id && t.name) nameById.set(t.id, t.name);
    });

    return rawLocations.map((r) => {
      const resolved = nameById.get(r.userId) || r.storedName || r.userId;
      const age = nowTick - new Date(r.lastSeen).getTime();
      return {
        userId: r.userId,
        name: resolved,
        role: r.role,
        lat: r.lat,
        lng: r.lng,
        lastSeen: r.lastSeen,
        accuracy: r.accuracy,
        isRecent: age < LOCATION_RECENT_MS,
        isOffline: age >= LOCATION_OFFLINE_MS,
      };
    });
  }, [rawLocations, team, nowTick]);

  return { locations, loading };
}
